'use client';

import ModalDialogue from '@koda/koda-components/molecules/ModalDialogue';
import NumberField from '@koda/koda-components/molecules/NumberField';
import { RadioGroup } from '@koda/koda-components/molecules/RadioGroup';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import {
  CHOIR_CONTENT_PATH,
  ChoirOrganization,
  choirOrganizations,
  choirSchema,
  createChoirDefaults,
} from './choir.Schema';

function ChoirComponent(): ReactElement {
  const licenseCode = LicenseTypes.Choir;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: choirSchema,
    defaults: createChoirDefaults,
  });

  const t = useTranslations(CHOIR_CONTENT_PATH);

  const handleContactUs = () => {
    const emailSubject = t('eventsExceededModal.emailSubject');
    const emailBody = t('eventsExceededModal.emailBody', {
      events: values.numberOfEventsPerYear,
      organization: values.organization ? t(`organization.options.${values.organization}`) : '-',
    });

    window.location.href = `mailto:kunde@koda.dk?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  };

  return (
    <>
      <NumberField
        label={t('numberOfEventsPerYear.label')}
        name="numberOfEventsPerYear"
        placeholder={t('numberOfEventsPerYear.placeholder')}
        required
        disabled={costCalculated}
        value={values.numberOfEventsPerYear}
        onChange={e => setField('numberOfEventsPerYear', parseInt(e.target.value))}
        error={showErrors ? getError('numberOfEventsPerYear') : undefined}
        validated={!!values.numberOfEventsPerYear && !getError('numberOfEventsPerYear')}
        helpText={t('numberOfEventsPerYear.helpText')}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <RadioGroup
        title={t('organization.label')}
        data-error-field={getError('organization') ? 'true' : undefined}
        options={choirOrganizations.map(organization => ({
          value: organization,
          label: t(`organization.options.${organization}`),
        }))}
        disabled={costCalculated}
        horizontal={false}
        className="gap-4"
        value={values.organization as string | undefined}
        onValueChange={value => setField('organization', value as ChoirOrganization)}
        error={showErrors ? getError('organization') : undefined}
      />
      <ModalDialogue
        title={t('eventsExceededModal.title')}
        description={t('eventsExceededModal.description')}
        type="info"
        primaryText={t('eventsExceededModal.contactUsButton')}
        cancelText={t('eventsExceededModal.correctInfoButton')}
        open={
          typeof values.numberOfEventsPerYear === 'number' &&
          !Number.isNaN(values.numberOfEventsPerYear) &&
          values.numberOfEventsPerYear > 13
        }
        onPrimaryAction={handleContactUs}
        onCancelAction={() => setField('numberOfEventsPerYear', undefined)}
        onOpenChange={isOpen => {
          if (!isOpen) setField('numberOfEventsPerYear', undefined);
        }}
      />
    </>
  );
}

export default ChoirComponent;
